import { SubscriptionUpdatedEvent, ThemeEvents } from '@theme/events';
import { IntelligemsDomEvents, dispatchIntelligemsEvent } from '@theme/intelligems-events';
import { formatMoney } from '@theme/utilities';

/**
 * Composant d'abonnement permettant de choisir entre achat unique et abonnement.
 * Gère l'affichage des prix, la sélection de fréquence et la mise à jour du formulaire produit.
 */
class SubscriptionComponent extends HTMLElement {
  constructor() {
    super();

    this.sellingPlanInput = null;
    this.oneTimeOption = null;
    this.subscriptionOption = null;
    this.oneTimeCard = null;
    this.subscriptionCard = null;
    this.frequencySelect = null;
    this.frequencyInputs = null;
    this.frequencyContainer = null;
    this.oneTimePriceContainer = null;
    this.subscriptionPriceContainer = null;
    this.subscriptionBadge = null;
    this.subscriptionBadgeValue = null;
    this.productForm = null;

    this.handleOneTimeChange = () => {
      this.#onOptionChange('one_time');
      dispatchIntelligemsEvent(this, IntelligemsDomEvents.subscriptionOfferSelected, { source: 'one_time' });
    };
    this.handleSubscriptionChange = () => {
      this.#onOptionChange('subscription');
      dispatchIntelligemsEvent(this, IntelligemsDomEvents.subscriptionOfferSelected, { source: 'subscription' });
    };
    this.handleFrequencyChange = () => this.#onFrequencyChange();
    this.handleFrequencyButtonChange = (event) => this.#onFrequencyButtonChange(event);
    this.handlePriceUpdate = () => this.#updateDisplayedPrices();
    this.handleVariantUpdate = (event) => this.#onVariantUpdate(event);
    this.handleFormUpdatedPrice = () => {
      if (!this.isConnected) return;
      this.#updateDisplayedPrices();
    };
  }

  connectedCallback() {
    this.#initDomReferences();

    this.oneTimeOption?.addEventListener('change', this.handleOneTimeChange);
    this.subscriptionOption?.addEventListener('change', this.handleSubscriptionChange);
    this.frequencySelect?.addEventListener('change', this.handleFrequencyChange);
    this.#addFrequencyButtonListeners();

    document.addEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);
    document.addEventListener(ThemeEvents.quantityBreaksUpdated, this.handlePriceUpdate);
    this.productForm?.addEventListener(ThemeEvents.formUpdated, this.handleFormUpdatedPrice);

    this.#initDefaultSelection();
    this.#updateFrequencySelectDisplay();
  }

  disconnectedCallback() {
    this.oneTimeOption?.removeEventListener('change', this.handleOneTimeChange);
    this.subscriptionOption?.removeEventListener('change', this.handleSubscriptionChange);
    this.frequencySelect?.removeEventListener('change', this.handleFrequencyChange);
    this.#removeFrequencyButtonListeners();

    document.removeEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);
    document.removeEventListener(ThemeEvents.quantityBreaksUpdated, this.handlePriceUpdate);
    this.productForm?.removeEventListener(ThemeEvents.formUpdated, this.handleFormUpdatedPrice);
  }

  /**
   * Initialise les références DOM du composant.
   */
  #initDomReferences() {
    this.sellingPlanInput = this.querySelector('[data-ref="selling-plan-input"]');
    this.oneTimeOption = this.querySelector('[data-ref="one-time-option"]');
    this.subscriptionOption = this.querySelector('[data-ref="subscription-option"]');
    this.oneTimeCard = this.querySelector('[data-ref="one-time-card"]');
    this.subscriptionCard = this.querySelector('[data-ref="subscription-card"]');
    this.frequencySelect = this.querySelector('[data-ref="frequency-select"]');
    this.frequencyInputs = this.querySelectorAll('[data-ref="frequency-input"]');
    this.frequencyContainer = this.querySelector('[data-ref="frequency-container"]');
    this.oneTimePriceContainer = this.querySelector('[data-ref="one-time-price"]');
    this.subscriptionPriceContainer = this.querySelector('[data-ref="subscription-price"]');
    this.subscriptionBadge = this.querySelector('[data-ref="subscription-badge"]');
    this.subscriptionBadgeValue = this.querySelector('[data-ref="subscription-badge-value"]');
    this.productForm = this.closest('[data-ref="product-section-content"]')?.querySelector('[data-ref="product-form"]') || this.closest('[data-ref="popup-content"]')?.querySelector('[data-ref="product-form"]');
  }

  /**
   * Portée du formulaire dans le document de réponse (évite le mauvais subscription-component sur la page).
   */
  #subscriptionScopeFromHtml(doc) {
    if (!doc || typeof doc.querySelector !== 'function') return null;
    return (
      doc.querySelector('.product-section product-form[data-ref="product-form"]') ||
      doc.querySelector('.product-section product-form') ||
      doc.querySelector('[data-ref="product-section-content"] product-form') ||
      doc.querySelector('product-form[data-ref="product-form"]') ||
      doc.querySelector('product-form')
    );
  }

  /**
   * Gère la mise à jour au changement de variante.
   * Re-rend le composant avec le HTML de la nouvelle variante.
   * @param {CustomEvent} event - L'événement de mise à jour de variante
   */
  #onVariantUpdate(event) {
    if (!event.detail?.data?.html) return;

    if (!this.#isRelevantVariantUpdate(event)) return;

    if (event.detail?.data?.skipMediaRefresh) {
      this.#initDomReferences();
      this.#updateDisplayedPrices();
      return;
    }

    const wasSubscriptionSelected = this.isSubscriptionSelected();
    const previousFrequency = this.#getSelectedFrequencyValue();

    const formScope = this.#subscriptionScopeFromHtml(event.detail.data.html);
    const newSubscription = formScope?.querySelector('subscription-component') ?? event.detail.data.html.querySelector('subscription-component');

    if (!newSubscription) {
      this.#initDomReferences();
      return;
    }

    const hasSubscriptionContent = newSubscription?.querySelector('[data-ref="selling-plan-input"]');

    // Si la nouvelle variante n'a pas d'abonnement, masquer le composant et vider le contenu
    if (!hasSubscriptionContent) {
      this.style.display = 'none';
      this.innerHTML = '';

      // Réinitialiser les références DOM à null pour que isSubscriptionSelected() retourne false
      this.sellingPlanInput = null;
      this.oneTimeOption = null;
      this.subscriptionOption = null;
      this.oneTimeCard = null;
      this.subscriptionCard = null;
      this.frequencySelect = null;
      this.frequencyInputs = null;
      this.frequencyContainer = null;
      this.oneTimePriceContainer = null;
      this.subscriptionPriceContainer = null;
      this.subscriptionBadge = null;
      this.subscriptionBadgeValue = null;

      this.dispatchEvent(new SubscriptionUpdatedEvent());
      return;
    }

    // Afficher le composant et mettre à jour le contenu
    this.style.display = '';
    this.innerHTML = newSubscription.innerHTML;

    // Retirer les anciens listeners avant de réinitialiser les références
    this.oneTimeOption?.removeEventListener('change', this.handleOneTimeChange);
    this.subscriptionOption?.removeEventListener('change', this.handleSubscriptionChange);
    this.frequencySelect?.removeEventListener('change', this.handleFrequencyChange);
    this.#removeFrequencyButtonListeners();

    // Réinitialiser les références DOM
    this.#initDomReferences();

    // Réattacher les listeners
    this.oneTimeOption?.addEventListener('change', this.handleOneTimeChange);
    this.subscriptionOption?.addEventListener('change', this.handleSubscriptionChange);
    this.frequencySelect?.addEventListener('change', this.handleFrequencyChange);
    this.#addFrequencyButtonListeners();

    // Réinitialiser la sélection par défaut
    this.#initDefaultSelection();

    if (wasSubscriptionSelected && this.subscriptionOption && this.isSubscriptionActivated()) {
      this.#restoreSubscriptionSelection(previousFrequency);
    }

    this.#updateFrequencySelectDisplay();
    this.#updateDisplayedPrices();
    this.#restoreSectionLabel();
    this.dispatchEvent(new SubscriptionUpdatedEvent());
  }

  /**
   * Le fetch variante réinjecte le label par défaut du schéma au lieu du réglage marchand.
   */
  #restoreSectionLabel() {
    const configuredLabel = this.dataset.sectionLabel?.trim() ?? '';
    const sectionLabel = this.querySelector('.variant_label');

    if (!configuredLabel || this.dataset.showSectionLabel !== 'true') {
      sectionLabel?.remove();
      return;
    }

    if (sectionLabel) {
      sectionLabel.textContent = configuredLabel;
      return;
    }

    const label = document.createElement('p');
    label.className = 'variant_label';
    label.textContent = configuredLabel;
    this.prepend(label);
  }

  /**
   * Limite la mise à jour au formulaire produit concerné par le changement de variante.
   * @param {CustomEvent} event
   * @returns {boolean}
   */
  #isRelevantVariantUpdate(event) {
    const productId = event.detail?.data?.productId;
    if (!productId) return true;

    this.#initDomReferences();
    const picker = this.productForm?.querySelector('variant-picker[data-product-id]');
    if (!picker) return true;

    return picker.dataset.productId === productId;
  }

  /**
   * Restaure l'abonnement après un fetch de variante.
   * @param {string} previousFrequency
   */
  #restoreSubscriptionSelection(previousFrequency) {
    if (!this.subscriptionOption) return;

    this.subscriptionOption.checked = true;
    this.#restoreFrequencySelection(previousFrequency);
    this.#onOptionChange('subscription', true);
  }

  /**
   * Restaure la fréquence d'abonnement si elle existe sur la nouvelle variante.
   * @param {string} previousFrequency
   */
  #restoreFrequencySelection(previousFrequency) {
    if (this.#shouldHideFrequencyPicker()) {
      this.#applyDefaultFrequencySelection();
      return;
    }

    if (!previousFrequency) return;

    if (this.frequencySelect) {
      const matchingOption = Array.from(this.frequencySelect.options).find((option) => option.value === previousFrequency);
      if (matchingOption) {
        this.frequencySelect.value = previousFrequency;
        return;
      }
    }

    const matchingInput = Array.from(this.frequencyInputs ?? []).find((input) => input.value === previousFrequency);
    if (matchingInput instanceof HTMLInputElement) {
      matchingInput.checked = true;
    }
  }

  /**
   * Initialise la sélection par défaut selon le paramètre data-default-selected.
   * Si le produit nécessite un abonnement (requires_selling_plan), l'abonnement est forcé.
   */
  #initDefaultSelection() {
    const requiresSellingPlan = this.dataset.requiresSellingPlan === 'true';

    // Cas où le produit nécessite un abonnement (pas d'option achat unique)
    if (requiresSellingPlan) {
      if (this.subscriptionOption && this.isSubscriptionActivated()) {
        this.subscriptionOption.checked = true;
        this.#onOptionChange('subscription', true);
      }
      return;
    }

    // Ne rien faire si le composant est vide (variante sans abonnement)
    if (!this.oneTimeOption) return;

    // Forcer one_time si les quantity breaks sont en mode "variant picker" (incompatible avec abonnement)
    if (this.#isQuantityBreaksVariantPickerActive()) {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time', true);
      return;
    }

    const defaultSelected = this.dataset.defaultSelected || 'one_time';

    if (defaultSelected === 'subscription' && this.subscriptionOption && this.isSubscriptionActivated()) {
      this.subscriptionOption.checked = true;
      this.#onOptionChange('subscription', true);
    } else {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time', true);
    }
  }

  /**
   * Vérifie si les quantity breaks sont en mode "sélecteur de variante".
   * Ce mode est incompatible avec les abonnements.
   * @returns {boolean}
   */
  #isQuantityBreaksVariantPickerActive() {
    const quantityBreaks = this.productForm?.querySelector('[data-ref="quantity-breaks"]');
    return quantityBreaks?.dataset.showVariantPicker === 'true';
  }

  /**
   * Gère le changement d'option (achat unique / abonnement).
   * @param {'one_time' | 'subscription'} optionType - Type d'option sélectionnée
   * @param {boolean} notify - Indique si l'événement de mise à jour doit être déclenché
   */
  #onOptionChange(optionType, notify = true) {
    this.#updateSelectedCard(optionType);

    if (optionType === 'subscription') {
      this.#applyDefaultFrequencySelection();

      if (this.#shouldHideFrequencyPicker()) {
        this.#hideFrequencySelect();
      } else {
        this.#showFrequencySelect();
      }

      this.#updateSellingPlanInput(this.#getSelectedFrequencyValue());
    } else {
      this.#hideFrequencySelect();
      this.#updateSellingPlanInput('');
    }

    this.#updateDisplayedPrices();
    if (notify) {
      this.dispatchEvent(new SubscriptionUpdatedEvent());
    }
  }

  /**
   * Indique si le sélecteur de fréquence est masqué via les paramètres du bloc.
   * @returns {boolean}
   */
  #shouldHideFrequencyPicker() {
    return this.dataset.hideFrequencyPicker === 'true';
  }

  /**
   * Sélectionne la fréquence configurée ou la première disponible.
   */
  #applyDefaultFrequencySelection() {
    let defaultId = this.dataset.defaultSellingPlanId || '';

    if (!this.#planExists(defaultId)) {
      defaultId = this.#getFirstFrequencyValue();
    }

    if (!defaultId) return;

    if (this.frequencySelect) {
      this.frequencySelect.value = defaultId;
      this.#updateFrequencySelectDisplay();
      return;
    }

    const matchingInput = Array.from(this.frequencyInputs ?? []).find((input) => input.value === defaultId);
    if (matchingInput instanceof HTMLInputElement) {
      matchingInput.checked = true;
    }
  }

  /**
   * @param {string} planId
   * @returns {boolean}
   */
  #planExists(planId) {
    if (!planId) return false;

    if (this.frequencySelect) {
      return Array.from(this.frequencySelect.options).some((option) => option.value === planId);
    }

    return Array.from(this.frequencyInputs ?? []).some((input) => input.value === planId);
  }

  /**
   * @returns {string}
   */
  #getFirstFrequencyValue() {
    if (this.frequencySelect?.options.length) {
      return this.frequencySelect.options[0].value;
    }

    return this.frequencyInputs?.[0]?.value ?? '';
  }

  /**
   * Gère le changement de fréquence d'abonnement.
   */
  #onFrequencyChange() {
    this.#updateFrequencySelectDisplay();
    this.#updateSellingPlanInput(this.frequencySelect?.value || '');
    this.#updateDisplayedPrices();
    this.dispatchEvent(new SubscriptionUpdatedEvent());
    dispatchIntelligemsEvent(this, IntelligemsDomEvents.subscriptionOfferSelected, { source: 'frequency' });
  }

  #updateFrequencySelectDisplay() {
    const select = this.frequencySelect;
    const display = this.querySelector('[data-ref="frequency-select-display"]');
    if (!select || !display) return;

    const opt = select.options[select.selectedIndex];
    if (!opt) return;

    const full = opt.dataset.labelFull;
    const text = full != null && full !== '' ? full : (opt.textContent ?? '').trim();
    display.textContent = text;

    const prefix = select.dataset.labelPrefix || '';
    if (text) {
      select.setAttribute('aria-label', prefix ? `${prefix}: ${text}` : text);
    } else {
      select.setAttribute('aria-label', prefix || '');
    }
  }

  /**
   * Met à jour les prix affichés dans les cartes one-time et abonnement.
   * Applique les réductions dans l'ordre Shopify : Abonnement → Quantity Breaks.
   * Les cross-sells sont ajoutés après les réductions (pas de réduction sur les cross-sells).
   */
  #updateDisplayedPrices() {
    const { priceAfterQB, priceBeforeQB, compareAtPrice, quantity, qbDiscount, crossSellPrice, crossSellCompareAtPrice } = this.#getBasePrices();
    if (priceAfterQB === null || priceAfterQB === undefined) return;

    // Carte one-time : prix après QB + cross-sells + compare-at original (sans badge)
    const oneTimeTotal = priceAfterQB + crossSellPrice;
    const oneTimeCompareAt = compareAtPrice + crossSellCompareAtPrice;
    const hideOneTimeCompareAt = this.dataset.hideOneTimeCompareAt === 'true';
    this.#updatePriceElement(
      this.oneTimePriceContainer,
      oneTimeTotal,
      hideOneTimeCompareAt ? oneTimeTotal : oneTimeCompareAt,
    );

    // Carte abonnement - Appliquer la réduction abo sur le prix de base (avant réduction QB)
    let subscriptionPrice = this.#calculateSubscriptionPrice(priceBeforeQB, quantity);

    if (qbDiscount) {
      if (qbDiscount.type === 'percentage' && qbDiscount.percentage > 0) {
        subscriptionPrice = Math.round((subscriptionPrice * (100 - qbDiscount.percentage)) / 100);
      } else if (qbDiscount.type === 'value' && qbDiscount.value > 0) {
        subscriptionPrice = subscriptionPrice - qbDiscount.value;
      }
    }

    // Ajouter les cross-sells après les réductions (pas de réduction sur les cross-sells)
    const subscriptionTotal = Math.max(0, subscriptionPrice) + crossSellPrice;
    // Prix barré = prix d'achat unique de la variante (pas le compare_at / MSRP)
    const subscriptionCompareAt = oneTimeTotal;

    // Carte abonnement : avec badge affichant la réduction de l'abonnement (minimum 0€)
    this.#updatePriceElement(this.subscriptionPriceContainer, subscriptionTotal, subscriptionCompareAt);
    this.#updateSubscriptionBadge(priceBeforeQB, quantity);
  }

  /**
   * Récupère les prix de base depuis les quantity breaks ou le product form.
   * @returns {{ priceAfterQB: number, priceBeforeQB: number, compareAtPrice: number, quantity: number, qbDiscount: object|null, crossSellPrice: number, crossSellCompareAtPrice: number }}
   */
  #getBasePrices() {
    // Source de vérité unique pour le prix de la variante : toujours à jour après product-form.onFormUpdated
    const addToCartContainer = this.productForm?.querySelector('[data-ref="add-to-cart-button-container"]');
    const variantUnitPrice = parseInt(addToCartContainer?.dataset.price) || 0;
    const variantUnitCompareAtPrice = parseInt(addToCartContainer?.dataset.compareAtPrice) || variantUnitPrice;

    const quantityBreaks = this.productForm?.querySelector('[data-ref="quantity-breaks"]');

    if (quantityBreaks && typeof quantityBreaks.getDiscount === 'function' && quantityBreaks.isActive?.()) {
      const quantity = parseInt(quantityBreaks.getSelectedQuantity?.()?.dataset?.quantity) || 1;
      const discount = quantityBreaks.getDiscount();

      // Prix avant réduction QB : prix unitaire de la variante × quantité du palier
      const priceBeforeQB = variantUnitPrice * quantity;

      // Calcul du prix après réduction QB, indépendamment des datasets potentiellement obsolètes
      let priceAfterQB = priceBeforeQB;
      if (discount?.type === 'percentage' && discount.percentage > 0) {
        priceAfterQB = Math.round((priceBeforeQB * (100 - discount.percentage)) / 100);
      } else if (discount?.type === 'value' && discount.value > 0) {
        priceAfterQB = Math.max(0, priceBeforeQB - discount.value);
      }

      // Cross-sells : pas de réduction abonnement appliquée dessus
      let crossSellPrice = 0;
      let crossSellCompareAtPrice = 0;
      if (typeof quantityBreaks.getCrossSellPrices === 'function') {
        const crossSellPrices = quantityBreaks.getCrossSellPrices();
        crossSellPrice = parseInt(crossSellPrices.price) || 0;
        crossSellCompareAtPrice = parseInt(crossSellPrices.compareAtPrice) || crossSellPrice;
      }

      return {
        priceAfterQB,
        priceBeforeQB,
        compareAtPrice: variantUnitCompareAtPrice * quantity,
        quantity,
        qbDiscount: discount,
        crossSellPrice,
        crossSellCompareAtPrice,
      };
    }

    return {
      priceAfterQB: variantUnitPrice,
      priceBeforeQB: variantUnitPrice,
      compareAtPrice: variantUnitCompareAtPrice,
      quantity: 1,
      qbDiscount: null,
      crossSellPrice: 0,
      crossSellCompareAtPrice: 0,
    };
  }

  /**
   * Calcule le prix avec la réduction abonnement appliquée.
   * @param {number} basePrice - Prix de base en centimes
   * @param {number} quantity - Nombre d'articles
   * @returns {number} Prix ajusté en centimes
   */
  #calculateSubscriptionPrice(basePrice, quantity = 1) {
    const adjustment = this.#getSelectedPlanAdjustment();
    return this.#applyPriceAdjustment(basePrice, adjustment, quantity);
  }

  /**
   * Récupère l'ajustement de prix du selling plan sélectionné.
   * @returns {{ type: string, value: number } | null}
   */
  #getSelectedPlanAdjustment() {
    const selectedElement = this.#getSelectedFrequencyElement();
    if (!selectedElement) return null;

    return {
      type: selectedElement.dataset.adjustmentType || 'percentage',
      value: parseFloat(selectedElement.dataset.adjustmentValue) || 0,
    };
  }

  /**
   * Applique un ajustement de prix (pourcentage, montant fixe ou prix fixe).
   * @param {number} basePrice - Prix de base en centimes
   * @param {{ type: string, value: number } | null} adjustment - Ajustement à appliquer
   * @param {number} quantity - Nombre d'articles (pour fixed_amount et price)
   * @returns {number} Prix ajusté en centimes
   */
  #applyPriceAdjustment(basePrice, adjustment, quantity = 1) {
    if (!adjustment) return basePrice;

    switch (adjustment.type) {
      case 'percentage':
        let unitBasePrice = basePrice / quantity;
        let unitDiscountAmount = (unitBasePrice * adjustment.value) / 100;
        let unitSubscriptionPrice = unitBasePrice - unitDiscountAmount;
        unitSubscriptionPrice = Math.round(unitSubscriptionPrice);
        let returnedPrice = unitSubscriptionPrice * quantity;
        return returnedPrice;
      case 'fixed_amount':
        return Math.max(0, basePrice - adjustment.value * quantity);
      case 'price':
        return adjustment.value * quantity;
      default:
        return basePrice;
    }
  }

  /**
   * Met à jour l'affichage d'un élément de prix (prix + prix barré + badge).
   * @param {HTMLElement | null} container - Conteneur du prix
   * @param {number} price - Prix à afficher en centimes
   * @param {number} compareAtPrice - Prix barré en centimes
   */
  #updatePriceElement(container, price, compareAtPrice) {
    if (!container) return;

    const priceEl = container.querySelector('[data-ref="price"]');
    const compareAtPriceEl = container.querySelector('[data-ref="compare-at-price"]');

    if (priceEl) {
      priceEl.textContent = formatMoney(price);
      priceEl.dataset.value = price;
    }

    const hasSavings = compareAtPrice > price;

    if (compareAtPriceEl) {
      if (hasSavings) {
        compareAtPriceEl.textContent = formatMoney(compareAtPrice);
        compareAtPriceEl.dataset.value = compareAtPrice;
      } else {
        compareAtPriceEl.textContent = '';
        compareAtPriceEl.dataset.value = '';
      }
    }
  }

  /**
   * Formate la réduction de l'abonnement pour le badge.
   * @returns {string} Réduction formatée (ex: "10%", "5,00 €")
   */
  #formatSubscriptionDiscount(basePrice, quantity = 1) {
    const adjustment = this.#getSelectedPlanAdjustment();
    const salesBadge = this.dataset.salesBadge || 'none';
    if (!adjustment || adjustment.value === 0 || salesBadge === 'none') return '';

    const subscriptionPrice = this.#calculateSubscriptionPrice(basePrice, quantity);
    const savings = Math.max(0, basePrice - subscriptionPrice);
    if (savings <= 0 || basePrice <= 0) return '';

    if (salesBadge === 'amount') {
      return formatMoney(savings);
    }

    return `-${Math.round((savings * 100) / basePrice)}%`;
  }

  #updateSubscriptionBadge(basePrice, quantity = 1) {
    if (!this.subscriptionBadge || !this.subscriptionBadgeValue) return;

    const badgeValue = this.#formatSubscriptionDiscount(basePrice, quantity);
    this.subscriptionBadgeValue.textContent = badgeValue;
    this.subscriptionBadge.classList.toggle('hidden', badgeValue === '');
  }

  /**
   * Met à jour la classe CSS de la carte sélectionnée.
   * @param {'one_time' | 'subscription'} optionType - Type d'option sélectionnée
   */
  #updateSelectedCard(optionType) {
    this.oneTimeCard?.classList.remove('subscription__option--selected');
    this.subscriptionCard?.classList.remove('subscription__option--selected');

    if (optionType === 'one_time') {
      this.oneTimeCard?.classList.add('subscription__option--selected');
    } else {
      this.subscriptionCard?.classList.add('subscription__option--selected');
    }
  }

  #showFrequencySelect() {
    if (this.#shouldHideFrequencyPicker()) return;

    if (this.frequencyContainer) {
      this.frequencyContainer.style.display = 'block';
    }

    this.#updateFrequencySelectDisplay();
  }

  #hideFrequencySelect() {
    if (this.frequencyContainer) {
      this.frequencyContainer.style.display = 'none';
    }
  }

  /**
   * Ajoute les event listeners pour les boutons de fréquence.
   */
  #addFrequencyButtonListeners() {
    if (!this.frequencyInputs) return;

    for (const input of this.frequencyInputs) {
      input.addEventListener('change', this.handleFrequencyButtonChange);
    }
  }

  /**
   * Retire les event listeners des boutons de fréquence.
   */
  #removeFrequencyButtonListeners() {
    if (!this.frequencyInputs) return;

    for (const input of this.frequencyInputs) {
      input.removeEventListener('change', this.handleFrequencyButtonChange);
    }
  }

  /**
   * Gère le changement de fréquence via bouton radio.
   */
  #onFrequencyButtonChange() {
    this.#updateSellingPlanInput(this.#getSelectedFrequencyValue());
    this.#updateDisplayedPrices();
    this.dispatchEvent(new SubscriptionUpdatedEvent());
    dispatchIntelligemsEvent(this, IntelligemsDomEvents.subscriptionOfferSelected, { source: 'frequency' });
  }

  /**
   * Récupère la valeur de fréquence sélectionnée (select ou bouton).
   * @returns {string}
   */
  #getSelectedFrequencyValue() {
    if (this.frequencySelect) {
      return this.frequencySelect.value || '';
    }

    if (this.frequencyInputs?.length > 0) {
      const checkedInput = this.querySelector('[data-ref="frequency-input"]:checked');
      return checkedInput?.value || '';
    }

    return '';
  }

  /**
   * Récupère l'élément de fréquence sélectionné (option ou input).
   * @returns {HTMLElement | null}
   */
  #getSelectedFrequencyElement() {
    if (this.frequencySelect) {
      return this.frequencySelect.options[this.frequencySelect.selectedIndex] || null;
    }

    if (this.frequencyInputs?.length > 0) {
      return this.querySelector('[data-ref="frequency-input"]:checked');
    }

    return null;
  }

  /**
   * Met à jour la valeur de l'input hidden selling_plan.
   * @param {string} value - ID du selling plan ou chaîne vide
   */
  #updateSellingPlanInput(value) {
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = value;
    }
  }

  /**
   * Récupère l'ID du selling plan sélectionné.
   * @returns {string} ID du selling plan ou chaîne vide
   */
  getSelectedSellingPlan() {
    return this.sellingPlanInput?.value || '';
  }

  /**
   * Indique si l'option abonnement est sélectionnée.
   * @returns {boolean}
   */
  isSubscriptionSelected() {
    return this.subscriptionOption?.checked || false;
  }

  /**
   * Indique si le composant est visible (non masqué par les quantity breaks).
   * @returns {boolean}
   */
  isSubscriptionActivated() {
    return this.style.display !== 'none';
  }

  /**
   * Récupère l'ajustement de prix du selling plan sélectionné.
   * @returns {{ type: string, value: number } | null} Null si achat unique sélectionné
   */
  getPriceAdjustment() {
    if (!this.isSubscriptionSelected()) return null;
    return this.#getSelectedPlanAdjustment();
  }

  /**
   * Calcule le prix ajusté en fonction de l'abonnement sélectionné.
   * Utilisé par product-form.js pour le calcul du prix total.
   * @param {number} basePrice - Prix de base en centimes
   * @param {number} quantity - Nombre d'articles
   * @returns {number} Prix ajusté en centimes
   */
  calculateAdjustedPrice(basePrice, quantity = 1) {
    return this.#applyPriceAdjustment(basePrice, this.getPriceAdjustment(), quantity);
  }
}

customElements.define('subscription-component', SubscriptionComponent);
